import { morph, MORPH_OPTIONS } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { loadStyleModules } from '@theme/seal-style-modules';

const quickAddTemplateScriptPromises = new Map();
const quickAddTemplateStylePromises = new Map();

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {AbortController | null} */
  #templateVariantScopeAbortController = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();
    const productHref = productLink?.href || this.dataset.productUrl;

    if (!productHref) return '';

    const url = new URL(productHref, window.location.origin);
    const productTemplateSuffix = this.dataset.productTemplateSuffix;

    if (productTemplateSuffix && !url.searchParams.has('view')) {
      url.searchParams.set('view', productTemplateSuffix);
    }

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || this.dataset.variantId || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#templateVariantScopeAbortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    const productTemplateSuffix = this.dataset.productTemplateSuffix || '';
    this.#showQuickAddSkeleton(productTemplateSuffix);
    this.#openQuickAddModal();

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    try {
      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          await this.#loadTemplateStyles(html);

          const templateContent = productTemplateSuffix ? this.#getTemplateModalContent(html) : null;
          const contentElement = templateContent || html.querySelector('[data-product-grid-content]');
          if (contentElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (contentElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        if (!productTemplateSuffix) {
          this.#updateVariantPicker(productGrid);
        }
      }
    } catch (error) {
      console.error(error);
      this.#showQuickAddError(productTemplateSuffix);
    }
  };

  /**
   * Opens the modal on an immediate skeleton while the product HTML is still loading.
   * @param {string} productTemplateSuffix
   */
  #showQuickAddSkeleton(productTemplateSuffix) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    if (productTemplateSuffix) {
      modalContent.dataset.productTemplateSuffix = productTemplateSuffix;
    } else {
      modalContent.removeAttribute('data-product-template-suffix');
    }

    modalContent.setAttribute('aria-busy', 'true');
    modalContent.innerHTML = `
      <div class="quick-add-modal__skeleton" data-quick-add-loading aria-hidden="true">
        <div class="quick-add-modal__skeleton-media">
          <div class="quick-add-modal__skeleton-block quick-add-modal__skeleton-image"></div>
          <div class="quick-add-modal__skeleton-thumbs">
            <span class="quick-add-modal__skeleton-block"></span>
            <span class="quick-add-modal__skeleton-block"></span>
            <span class="quick-add-modal__skeleton-block"></span>
            <span class="quick-add-modal__skeleton-block"></span>
          </div>
        </div>
        <div class="quick-add-modal__skeleton-details">
          <div class="quick-add-modal__skeleton-block quick-add-modal__skeleton-title"></div>
          <div class="quick-add-modal__skeleton-block quick-add-modal__skeleton-title quick-add-modal__skeleton-title--short"></div>
          <div class="quick-add-modal__skeleton-block quick-add-modal__skeleton-price"></div>
          <div class="quick-add-modal__skeleton-rule"></div>
          <div class="quick-add-modal__skeleton-stack">
            <span class="quick-add-modal__skeleton-block"></span>
            <span class="quick-add-modal__skeleton-block"></span>
            <span class="quick-add-modal__skeleton-block"></span>
          </div>
          <div class="quick-add-modal__skeleton-block quick-add-modal__skeleton-button"></div>
          <div class="quick-add-modal__skeleton-block quick-add-modal__skeleton-button quick-add-modal__skeleton-button--secondary"></div>
        </div>
      </div>
    `;
    modalContent.scrollTo({ top: 0, behavior: 'instant' });
  }

  /**
   * @param {string} productTemplateSuffix
   */
  #showQuickAddError(productTemplateSuffix) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    if (productTemplateSuffix) {
      modalContent.dataset.productTemplateSuffix = productTemplateSuffix;
    } else {
      modalContent.removeAttribute('data-product-template-suffix');
    }

    modalContent.removeAttribute('aria-busy');

    const errorContainer = document.createElement('div');
    errorContainer.className = 'quick-add-modal__load-error';
    errorContainer.setAttribute('role', 'status');

    const errorMessage = this.dataset.quickAddLoadError || '';
    if (errorMessage) {
      const message = document.createElement('p');
      message.textContent = errorMessage;
      errorContainer.append(message);
    }

    modalContent.replaceChildren(errorContainer);
  }

  /**
   * Gets product-information plus additional top-level product template sections for template-based quickviews.
   * @param {Document} html
   * @returns {Element | null}
   */
  #getTemplateModalContent(html) {
    const productInformation = html.querySelector('[data-testid="product-information"]');
    if (!productInformation) return null;

    const templateContent = document.createElement('div');
    templateContent.appendChild(productInformation.cloneNode(true));

    const productInformationSection = productInformation.closest('.shopify-section');
    let nextSection = productInformationSection?.nextElementSibling || null;

    while (nextSection) {
      if (nextSection instanceof HTMLElement && nextSection.classList.contains('shopify-section')) {
        templateContent.appendChild(nextSection.cloneNode(true));
      }

      nextSection = nextSection.nextElementSibling;
    }

    return templateContent;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const modalContent = dialogComponent.querySelector('#quick-add-modal-content');
    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;
    modalContent.removeAttribute('aria-busy');

    const productTemplateSuffix = this.dataset.productTemplateSuffix || '';
    if (productTemplateSuffix) {
      modalContent.dataset.productTemplateSuffix = productTemplateSuffix;
    } else {
      modalContent.removeAttribute('data-product-template-suffix');
    }

    if (isMobileBreakpoint() && !productTemplateSuffix) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    if (productTemplateSuffix) {
      this.#scopeTemplateVariantPickers(productGrid, productTemplateSuffix);
      await this.#loadTemplateScripts(productGrid);

      const templateContent = document.createElement('div');
      templateContent.append(...Array.from(productGrid.children));
      morph(modalContent, templateContent);
      this.#upgradeTemplateCustomElements(modalContent);
      this.#bindTemplateVariantScope(modalContent, productTemplateSuffix);
    } else {
      this.#templateVariantScopeAbortController?.abort();
      this.#templateVariantScopeAbortController = null;
      morph(modalContent, productGrid);
    }

    this.#syncVariantSelection(modalContent);
    this.#hydrateJudgeMePreviewBadges(modalContent);
    if (productTemplateSuffix) {
      this.#scopeTemplateVariantPickers(modalContent, productTemplateSuffix);
      this.#upgradeTemplateCustomElements(modalContent);
      this.#hydrateTemplateCustomElements(modalContent);
    }
    modalContent.scrollTo({ top: 0, behavior: 'instant' });
  }

  /**
   * Ensures custom elements parsed from fetched quickview HTML are upgraded before we trigger variant syncs.
   * @param {Element} templateContent
   */
  #upgradeTemplateCustomElements(templateContent) {
    if (window.customElements && typeof window.customElements.upgrade === 'function') {
      window.customElements.upgrade(templateContent);
    }

    templateContent.querySelectorAll('variant-picker, media-gallery').forEach((element) => {
      if (element instanceof Component) {
        element.updatedCallback();
      }
    });
  }

  /**
   * Ensures variant updates inside a template quickview fetch the same template view as the modal.
   * @param {Element} templateContent
   * @param {string} productTemplateSuffix
   */
  #scopeTemplateVariantPickers(templateContent, productTemplateSuffix) {
    if (!productTemplateSuffix) return;

    templateContent.querySelectorAll('variant-picker[data-product-url]').forEach((variantPicker) => {
      if (!(variantPicker instanceof HTMLElement) || !variantPicker.dataset.productUrl) return;

      const url = new URL(variantPicker.dataset.productUrl, window.location.origin);
      url.searchParams.set('view', productTemplateSuffix);
      variantPicker.dataset.productUrl = `${url.pathname}${url.search}`;
    });
  }

  /**
   * Re-applies quickview template scoping after native variant-picker morphs itself.
   * @param {Element} modalContent
   * @param {string} productTemplateSuffix
   */
  #bindTemplateVariantScope(modalContent, productTemplateSuffix) {
    this.#templateVariantScopeAbortController?.abort();
    this.#templateVariantScopeAbortController = new AbortController();

    modalContent.addEventListener(
      'change',
      (event) => {
        this.#handleTemplateVariantChange(event, modalContent, productTemplateSuffix);
      },
      { capture: true, signal: this.#templateVariantScopeAbortController.signal }
    );

    modalContent.addEventListener(
      'variant:update',
      () => {
        window.requestAnimationFrame(() => {
          this.#scopeTemplateVariantPickers(modalContent, productTemplateSuffix);
          this.#upgradeTemplateCustomElements(modalContent);
          this.#hydrateTemplateCustomElements(modalContent);
          this.#hydrateJudgeMePreviewBadges(modalContent);
        });
      },
      { signal: this.#templateVariantScopeAbortController.signal }
    );
  }

  /**
   * Handles variant picker changes inside template quickviews when dynamically morphed custom elements are not yet fully active.
   * @param {Event} event
   * @param {Element} modalContent
   * @param {string} productTemplateSuffix
   */
  #handleTemplateVariantChange(event, modalContent, productTemplateSuffix) {
    const selectedOption = event.target;
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLSelectElement)) return;

    const variantPicker = selectedOption.closest('variant-picker');
    if (!variantPicker || !modalContent.contains(variantPicker)) return;

    event.stopPropagation();
    this.#updateTemplateVariant(variantPicker, selectedOption, modalContent, productTemplateSuffix);
  }

  /**
   * @param {Element} variantPicker
   * @param {HTMLInputElement | HTMLSelectElement} selectedOption
   * @param {Element} modalContent
   * @param {string} productTemplateSuffix
   */
  async #updateTemplateVariant(variantPicker, selectedOption, modalContent, productTemplateSuffix) {
    const selectedOptionId = this.#getTemplateSelectedOptionId(selectedOption);
    if (!selectedOptionId) return;

    this.#syncTemplateVariantControlState(variantPicker, selectedOption);
    variantPicker.dispatchEvent(new VariantSelectedEvent({ id: selectedOptionId }));

    const requestUrl = this.#buildTemplateVariantRequestUrl(variantPicker, productTemplateSuffix);
    if (!requestUrl) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(requestUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Failed to fetch quickview variant: HTTP error ${response.status}`);

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (!textContent) return;

      const resource = JSON.parse(textContent);
      this.#updateTemplateVariantPicker(variantPicker, html, productTemplateSuffix);
      this.#updateTemplateMediaGallery(modalContent, html);
      this.#syncTemplateProductForms(modalContent, resource?.id);

      const currentVariantPicker = modalContent.querySelector('variant-picker') || variantPicker;
      currentVariantPicker.dispatchEvent(
        new VariantUpdateEvent(resource, selectedOptionId, {
          html,
          productId: currentVariantPicker instanceof HTMLElement ? currentVariantPicker.dataset.productId ?? '' : '',
        })
      );
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement} selectedOption
   * @returns {string}
   */
  #getTemplateSelectedOptionId(selectedOption) {
    if (selectedOption instanceof HTMLSelectElement) {
      return selectedOption.options[selectedOption.selectedIndex]?.dataset.optionValueId || '';
    }

    return selectedOption.dataset.optionValueId || '';
  }

  /**
   * @param {Element} variantPicker
   * @param {HTMLInputElement | HTMLSelectElement} selectedOption
   */
  #syncTemplateVariantControlState(variantPicker, selectedOption) {
    if (selectedOption instanceof HTMLInputElement) {
      if (selectedOption.type !== 'radio') return;

      const fieldset = selectedOption.closest('fieldset');
      fieldset?.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;

        input.dataset.previousChecked = input.dataset.currentChecked === 'true' ? 'true' : 'false';
        input.dataset.currentChecked = input === selectedOption ? 'true' : 'false';
        input.checked = input === selectedOption;
      });

      return;
    }

    Array.from(selectedOption.options).forEach((option) => {
      if (option.selected) {
        option.setAttribute('selected', 'selected');
      } else {
        option.removeAttribute('selected');
      }
    });
  }

  /**
   * @param {Element} variantPicker
   * @param {string} productTemplateSuffix
   * @returns {string}
   */
  #buildTemplateVariantRequestUrl(variantPicker, productTemplateSuffix) {
    if (!(variantPicker instanceof HTMLElement)) return '';

    const productUrl = variantPicker.dataset.productUrl || window.location.pathname;
    const url = new URL(productUrl, window.location.origin);
    if (productTemplateSuffix && !url.searchParams.has('view')) {
      url.searchParams.set('view', productTemplateSuffix);
    }

    const selectedOptionValues = this.#getTemplateSelectedOptionValues(variantPicker);
    if (selectedOptionValues.length) {
      url.searchParams.set('option_values', selectedOptionValues.join(','));
    }

    return `${url.pathname}${url.search}`;
  }

  /**
   * @param {Element} variantPicker
   * @returns {string[]}
   */
  #getTemplateSelectedOptionValues(variantPicker) {
    const values = [];

    variantPicker.querySelectorAll('fieldset').forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement && checked.dataset.optionValueId) {
        values.push(checked.dataset.optionValueId);
      }
    });

    variantPicker.querySelectorAll('select').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      const selected = select.options[select.selectedIndex];
      if (selected?.dataset.optionValueId) values.push(selected.dataset.optionValueId);
    });

    return values;
  }

  /**
   * @param {Element} variantPicker
   * @param {Document} html
   * @param {string} productTemplateSuffix
   */
  #updateTemplateVariantPicker(variantPicker, html, productTemplateSuffix) {
    const newVariantPicker = html.querySelector('variant-picker');
    if (!newVariantPicker) return;

    this.#scopeTemplateVariantPickers(html, productTemplateSuffix);

    if ('updateVariantPicker' in variantPicker && typeof variantPicker.updateVariantPicker === 'function') {
      variantPicker.updateVariantPicker(html);
      return;
    }

    morph(variantPicker, newVariantPicker, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.dataset.key;
      },
    });
  }

  /**
   * @param {Element} modalContent
   * @param {Document} html
   */
  #updateTemplateMediaGallery(modalContent, html) {
    const mediaGallery = modalContent.querySelector('media-gallery');
    const newMediaGallery = html.querySelector('media-gallery');
    if (!mediaGallery || !newMediaGallery) return;

    mediaGallery.replaceWith(newMediaGallery);
    this.#upgradeTemplateCustomElements(modalContent);
  }

  /**
   * @param {Element} modalContent
   * @param {string | number | undefined} variantId
   */
  #syncTemplateProductForms(modalContent, variantId) {
    if (!variantId) return;

    modalContent.querySelectorAll('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = String(variantId);
    });
  }

  /**
   * Lets template-specific custom elements rebuild after Horizon morphs cached quickview markup into an existing modal.
   * @param {Element} modalContent
   */
  #hydrateTemplateCustomElements(modalContent) {
    modalContent.querySelectorAll([
      'seal-ecoseal-variant-picker',
      'seal-ecoseal-mix-picker',
      'seal-fermentation-variant-picker',
    ].join(',')).forEach((picker) => {
      if ('reinitialize' in picker && typeof picker.reinitialize === 'function') {
        picker.reinitialize();
      }
    });
  }

  /**
   * Judge.me only initializes preview badges present on the initial page load.
   * Template quickviews are morphed in later, so reveal and localize their server-rendered counts here.
   * @param {Element} modalContent
   */
  #hydrateJudgeMePreviewBadges(modalContent) {
    modalContent.querySelectorAll('.jdgm-widget.jdgm-preview-badge').forEach((widget) => {
      if (!(widget instanceof HTMLElement)) return;

      const badge = widget.querySelector('.jdgm-prev-badge');
      if (!(badge instanceof HTMLElement)) return;

      const text = badge.querySelector('.jdgm-prev-badge__text');
      widget.classList.add('jdgm--done-setup', 'jdgm-preview-badge--with-link');
      widget.dataset.widgetName = widget.dataset.widgetName || 'preview_badge';
      badge.style.display = '';

      if (!(text instanceof HTMLElement)) return;

      const reviewCount = Number(badge.dataset.numberOfReviews || '');
      if (Number.isFinite(reviewCount)) {
        text.textContent = this.#formatReviewCount(reviewCount);
      }

      text.style.visibility = 'visible';
    });
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #formatReviewCount(count) {
    const locale = document.documentElement.lang || navigator.language || 'de';
    let formattedCount = String(count);

    try {
      formattedCount = new Intl.NumberFormat(locale).format(count);
    } catch (error) {
      formattedCount = String(count);
    }

    const label =
      count === 1 ? Theme.translations?.review_count_one || '' : Theme.translations?.review_count_other || '';

    return [formattedCount, label].filter(Boolean).join(' ');
  }

  /**
   * Loads stylesheet links required by dynamically fetched quickview templates.
   * @param {Document} html
   */
  async #loadTemplateStyles(html) {
    await loadStyleModules(html);
    const links = Array.from(html.querySelectorAll('link[rel="stylesheet"][href]:not([data-seal-style-module])'));
    if (!links.length) return;

    await Promise.all(links.map((link) => this.#loadTemplateStyle(link)));
  }

  /**
   * @param {HTMLLinkElement} sourceLink
   * @returns {Promise<void>}
   */
  #loadTemplateStyle(sourceLink) {
    const href = sourceLink.getAttribute('href');
    if (!href) return Promise.resolve();

    const url = new URL(href, window.location.href).href;
    const styleAlreadyLoaded = Array.from(document.querySelectorAll('link[rel="stylesheet"][href]')).some((link) => {
      return new URL(link.getAttribute('href') || '', window.location.href).href === url;
    });
    if (styleAlreadyLoaded) return Promise.resolve();

    const existingPromise = quickAddTemplateStylePromises.get(url);
    if (existingPromise) return existingPromise;

    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.media = sourceLink.media || 'all';
      link.fetchPriority = sourceLink.getAttribute('fetchpriority') || 'low';
      link.dataset.quickAddTemplateStyle = 'true';
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener(
        'error',
        () => {
          quickAddTemplateStylePromises.delete(url);
          reject(new Error(`Failed to load quickview template stylesheet: ${url}`));
        },
        { once: true }
      );

      document.head.append(link);
    });

    quickAddTemplateStylePromises.set(url, promise);
    return promise;
  }

  /**
   * Executes external scripts that arrive inside dynamically fetched quickview templates.
   * @param {Element} templateContent
   */
  async #loadTemplateScripts(templateContent) {
    const scripts = Array.from(templateContent.querySelectorAll('script[src]'));
    if (!scripts.length) return;

    await Promise.all(scripts.map((script) => this.#loadTemplateScript(script)));
  }

  /**
   * @param {HTMLScriptElement} sourceScript
   * @returns {Promise<void>}
   */
  #loadTemplateScript(sourceScript) {
    const src = sourceScript.getAttribute('src');
    if (!src) return Promise.resolve();

    const url = new URL(src, window.location.href).href;
    const existingPromise = quickAddTemplateScriptPromises.get(url);
    if (existingPromise) return existingPromise;

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.type = sourceScript.type || 'text/javascript';
      script.async = true;
      script.fetchPriority = sourceScript.getAttribute('fetchpriority') || 'low';
      script.dataset.quickAddTemplateScript = 'true';
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener(
        'error',
        () => {
          quickAddTemplateScriptPromises.delete(url);
          reject(new Error(`Failed to load quickview template script: ${url}`));
        },
        { once: true }
      );

      document.head.append(script);
    });

    quickAddTemplateScriptPromises.set(url, promise);
    return promise;
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
